// src/utils/richtext/sanitizeRichText.js
// 零依赖富文本净化器(白名单式)——把管理端富文本编辑器(RichTagEditor)产出的 HTML 收窄到
// 一小撮安全标签后才允许 v-html 渲染。防的是「低权限分店管理员 → 跨店顾客端存储型 XSS」:
// 被攻陷的分店后台可在取餐说明 / 多店说明 / 欢迎弹窗里注入 <img onerror> / <script> /
// <iframe> / javascript: 链接,在每个下单顾客的浏览器会话里执行,读取 sessionStorage /
// localStorage 里的顾客信息并外传。
//
// 为什么白名单而非黑名单:黑名单永远漏(新标签 / 新事件属性 / 编码绕过);白名单只放行
// 明确安全的一小撮,其余一律剥掉。为什么不引 DOMPurify:本仓不为此新增 HTML 净化依赖,
// 这类说明富文本只需换行 / 加粗 / 列表 / 链接几类,一个 DOMParser 遍历足够,零新增依赖。
//
// 移植自 frontend-kiosk-2026 src/features/richtext/sanitizeRichText.ts(commit 2519445)。

// 允许保留的标签(取餐 / 说明类富文本只需这些排版标签)。
const ALLOWED_TAGS = new Set([
  'P', 'BR', 'B', 'STRONG', 'I', 'EM', 'U', 'SPAN', 'UL', 'OL', 'LI', 'A'
])

// 允许保留的属性(仅 <a href>,且 href 还要过 URL 白名单)。
const ALLOWED_ATTRS = new Set(['href'])

// 危险容器:连同内容整块删除(不解封,避免把 JS/CSS/嵌入内容文本吐出来)。
// 单一真源——richTextContent(判空 / 取纯文本)复用同一份,保证「取纯文本」与「净化后 v-html」
// 对同一段富文本的可见性判断一致(否则 <iframe>x</iframe> 会在净化那档隐藏、在紧凑卡纯文本那档露出)。
export const DANGEROUS_CONTAINER_TAGS = new Set([
  'SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'SVG', 'MATH', 'LINK', 'META', 'TEMPLATE'
])

// 供 DOM querySelectorAll 复用的小写选择器(HTML 标签名匹配大小写不敏感)。
export const DANGEROUS_CONTAINER_SELECTOR = Array.from(DANGEROUS_CONTAINER_TAGS)
  .map((t) => t.toLowerCase())
  .join(', ')

// href 只放行 http(s) 与站内相对 / 锚点;javascript: / data: / vbscript: 等一律剥掉。
function isSafeHref(value) {
  const v = value.trim()
  if (v === '') return false
  // 浏览器解析 URL 前会先剥掉全部 Tab/LF/CR(WHATWG URL 规范),再把特殊 scheme 里的
  // 反斜杠当正斜杠;这里按同样顺序两步归一后再判,否则 /<TAB>/host 或 /\host 会被下面
  // 「相对路径」这条放行,点开却跳到 //host 外站(绕过 // 拦截)。
  const norm = v.replace(/[\t\n\r]/g, '').replace(/\\/g, '/')
  // 协议相对 URL(//host/…)会被浏览器解析成 https://host,不是站内相对路径 → 拒绝
  // (防被攻陷的分店后台在说明里塞跳去外站的链接)。
  if (norm.startsWith('//')) return false
  // 相对路径 / 锚点 / 查询串是安全的(没有协议)。
  if (/^(#|\/|\.\/|\?)/.test(norm)) return true
  // 有协议时只放行 http/https。
  return /^https?:\/\//i.test(norm)
}

// 递归清洗:白名单外的元素被「拆封」(去掉标签、保留子节点文本),脚本 / 样式类整块删除,
// 事件处理器与非白名单属性剥掉,危险 href 剥掉。
function cleanElement(el, doc) {
  // 先清子节点(用静态数组,因为下面会改动 childNodes)。
  const children = Array.from(el.childNodes)
  for (const child of children) {
    cleanNode(child, doc)
  }
}

function cleanNode(node, doc) {
  // 文本节点:原样保留。
  if (node.nodeType === 3 /* TEXT_NODE */) return

  // 非元素(注释、CDATA 等):直接删。
  if (node.nodeType !== 1 /* ELEMENT_NODE */) {
    node.remove()
    return
  }

  const el = node
  const tag = el.tagName.toUpperCase()

  // 脚本 / 样式 / 内嵌框架等危险容器:连同内容整块删除(不解封,避免把 JS/CSS 文本吐出来)。
  if (DANGEROUS_CONTAINER_TAGS.has(tag)) {
    el.remove()
    return
  }

  // 先递归清子树。
  cleanElement(el, doc)

  if (!ALLOWED_TAGS.has(tag)) {
    // 非白名单标签(如 <img>/<div>/<h1>):拆封——用其(已清洗的)子节点替换掉自身,
    // 保留可读文本,丢掉标签本身及其属性(含 onerror 等)。
    const parent = el.parentNode
    if (parent) {
      while (el.firstChild) parent.insertBefore(el.firstChild, el)
    }
    el.remove()
    return
  }

  // 白名单标签:逐个审属性,剥掉所有非白名单属性(含全部 on* 事件处理器)。
  for (const attr of Array.from(el.attributes)) {
    const name = attr.name.toLowerCase()
    if (!ALLOWED_ATTRS.has(name)) {
      el.removeAttribute(attr.name)
      continue
    }
    if (name === 'href' && !isSafeHref(attr.value)) {
      el.removeAttribute(attr.name)
    }
  }
}

/**
 * 把管理端富文本 HTML 收窄到安全白名单后返回。
 * 放行:p / br / b / strong / i / em / u / span / ul / ol / li / a[href=http(s)|相对|锚点]。
 * 剥掉:script / style / iframe / svg 等危险容器、全部事件处理器属性、javascript:/data: 链接;
 *      其余未知标签「拆封」保留文本。
 * 纯文本原样返回。null/undefined/空 → 空串。
 * @param {string|null|undefined} html
 * @returns {string}
 */
export function sanitizeRichText(html) {
  if (html == null) return ''
  const str = String(html)
  if (str === '') return ''

  // DOMParser 解析出的 body 不会执行任何脚本、不加载任何资源(不触发 <img>/onerror),
  // 因此解析阶段本身是安全的;我们只是拿它的树结构来做白名单裁剪。
  const doc = new DOMParser().parseFromString(str, 'text/html')
  const body = doc.body
  if (!body) return ''

  cleanElement(body, doc)
  return body.innerHTML
}
